import { useCallback, useMemo } from 'react';

import { FavoriteItemDto, TeamDto } from 'utils/api/dto';
import { FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import { FavoritePlayer, FavoriteTeam } from '../search-dropdown/favorites-section';
import { FavoriteItemType } from '../search-dropdown/types';

const EMPTY_ITEMS: FavoriteItemDto[] = [];
const EMPTY_TEAMS: TeamDto[] = [];

export type UseSearchFavoritesParams = {
    favPlayerList?: FavoriteItemDto[];
    favTeamList?: FavoriteItemDto[];
    /** the profiles of the favorite players */
    favorites: FavoritePlayer[];
    allTeams?: TeamDto[];
};

const mapByEntityId = (items: FavoriteItemDto[]) => new Map(items.map(item => [item.itemEntityId, item]));

/** The favorite teams to show and the favorite id of a found player or team (for its star) */
export const useSearchFavorites = ({
    favPlayerList = EMPTY_ITEMS,
    favTeamList = EMPTY_ITEMS,
    favorites,
    allTeams = EMPTY_TEAMS
}: UseSearchFavoritesParams) => {
    const playerItemsById = useMemo(() => mapByEntityId(favPlayerList), [favPlayerList]);
    const teamItemsById = useMemo(() => mapByEntityId(favTeamList), [favTeamList]);
    const favoriteIds = useMemo(() => new Set(favorites.map(({ id }) => id)), [favorites]);

    const favoriteTeams = useMemo(() => {
        const teamsById = new Map(allTeams.map(team => [team.id, team]));

        return favTeamList
            .map(({ itemEntityId }) => teamsById.get(itemEntityId))
            .filter((team): team is FavoriteTeam => team?.id != null);
    }, [favTeamList, allTeams]);

    // The id passed to onFavoriteToggle: the favorite record, or the player itself when only its profile is known
    const getFavoriteId = useCallback(
        (id: number, type: FavoriteItemType) => {
            if (type === FAV_AND_RECENT_TYPE_TEAM) {
                return teamItemsById.get(id)?.id;
            }

            return playerItemsById.get(id)?.id ?? (favoriteIds.has(id) ? id : undefined);
        },
        [teamItemsById, playerItemsById, favoriteIds]
    );

    return { favoriteTeams, getFavoriteId };
};
